'use client';

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@tranquility-ai/theme-components/button';
import { FullScreenContext } from '@/app/contexts';
import { BELOW_MAIN_NAV_SAFE_AREA_CLASS, EVIDENCE_LOCKER_SHELL_SIZE_CLASS } from './evidence-locker-preview-overlay';
import { EvidenceLockerPanelNew } from './evidence-locker-panel-new';
import type { EventFromChat } from './answer-item';
import { useTimePilotT } from '@/i18n/useTimePilotT';

export type EvidenceLockerModalNewProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Omit on surfaces with no timeline (HelioTrace); the create-event actions are then hidden. */
  addToTimeline?: (event: EventFromChat) => void;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  isPrimaryActionDisabled?: boolean;
};

export function EvidenceLockerModalNew({
  isOpen,
  onClose,
  addToTimeline,
  primaryActionLabel,
  onPrimaryAction,
  isPrimaryActionDisabled = false,
}: EvidenceLockerModalNewProps) {
  const t = useTimePilotT();
  const [mounted, setMounted] = useState(false);
  const [showFileTypeFilters, setShowFileTypeFilters] = useState(false);
  const isPreviewPopoverOpenRef = useRef(false);
  const { isFullscreen } = useContext(FullScreenContext);

  const handlePreviewPopoverOpenChange = useCallback((open: boolean) => {
    isPreviewPopoverOpenRef.current = open;
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setShowFileTypeFilters(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // The app fullscreen viewer sits above this modal and takes Escape while it
      // is up, the same way the preview overlay stands down for it.
      if (e.key !== 'Escape' || e.defaultPrevented || isFullscreen) return;
      // Claim the key only when this modal is the one closing: the preview popover
      // handles its own Escape, and marking the event on its behalf would leave
      // whatever is above it seeing a key that nothing acted on.
      if (isPreviewPopoverOpenRef.current) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, isFullscreen]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="evidence-locker-backdrop"
            aria-hidden
            className="fixed inset-0 z-[10050] bg-black/20 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onMouseDown={(e) => {
              if (e.target !== e.currentTarget) return;
              if (isPreviewPopoverOpenRef.current) return;
              onClose();
            }}
          />
          <div
            className={`${BELOW_MAIN_NAV_SAFE_AREA_CLASS} z-[10051] flex items-center justify-center p-3 pointer-events-none`}
          >
            <motion.div
              key="evidence-locker-panel"
              role="dialog"
              aria-modal="true"
              aria-label={t('chat.evidence.locker')}
              data-question-bar-interactive
              className={`pointer-events-auto flex max-h-full flex-col overflow-hidden rounded-lg bg-white text-black shadow-lg ${EVIDENCE_LOCKER_SHELL_SIZE_CLASS}`}
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-0 pb-4 px-0">
                <EvidenceLockerPanelNew
                  isActive={isOpen}
                  addToTimeline={addToTimeline}
                  onAppliedSelection={onClose}
                  onClose={onClose}
                  onPreviewPopoverOpenChange={handlePreviewPopoverOpenChange}
                  showFileTypeFilters={showFileTypeFilters}
                  onToggleFileTypeFilters={() => setShowFileTypeFilters((v) => !v)}
                  treeHeightClassName="min-h-0 flex-1"
                />
                {primaryActionLabel && onPrimaryAction ? (
                  <div className="flex flex-shrink-0 justify-end border-t border-black/10 px-4 pt-4">
                    <Button
                      type="button"
                      style="solid"
                      color="accent"
                      background="default"
                      size="sm"
                      disabled={isPrimaryActionDisabled}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isPrimaryActionDisabled) {
                          onPrimaryAction();
                        }
                      }}
                    >
                      {primaryActionLabel}
                    </Button>
                  </div>
                ) : null}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
